import React, { useRef, useState } from "react";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Chip from "@material-ui/core/Chip";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import Collapse from "@material-ui/core/Collapse";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(() => ({
	card: {
		marginTop: 8,
		padding: 10,
		display: "flex",
		flexDirection: "column",
		gap: 6,
		cursor: "pointer",
	},
	topRow: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "flex-start",
		gap: 8,
	},
	headingText: {
		flex: 1,
		minWidth: 0,
	},
	badgesRow: {
		display: "flex",
		gap: 6,
		flexWrap: "wrap",
		marginTop: 4,
	},
	valuesRow: {
		display: "flex",
		flexDirection: "column",
		gap: 1,
		marginTop: 2,
		padding: 6,
		borderRadius: 6,
		background: "#f1f5f9",
	},
	orderValue: {
		fontWeight: 700,
		color: "#0f172a",
	},
	details: {
		marginTop: 6,
		paddingTop: 6,
		borderTop: "1px solid rgba(0, 0, 0, 0.08)",
		display: "flex",
		flexDirection: "column",
		gap: 2,
	},
	action: {
		marginTop: 6,
		alignSelf: "flex-start",
	},
	attachArea: {
		marginTop: 8,
		paddingTop: 8,
		borderTop: "1px dashed rgba(0, 0, 0, 0.12)",
		display: "flex",
		flexDirection: "column",
		gap: 4,
	},
	attachSelect: {
		alignSelf: "flex-start",
		maxWidth: "100%",
		padding: "6px 8px",
		borderRadius: 4,
		border: "1px solid rgba(0, 0, 0, 0.23)",
		background: "#fff",
		fontSize: "0.875rem",
	},
	attachButton: {
		alignSelf: "flex-start",
	},
	attachFileRow: {
		display: "flex",
		alignItems: "center",
		gap: 8,
		flexWrap: "wrap",
	},
	attachFileName: {
		flex: 1,
		minWidth: 0,
		wordBreak: "break-all",
	},
	checkRow: {
		display: "flex",
		alignItems: "flex-start",
		gap: 6,
		cursor: "pointer",
	},
}));

// Tipos de anexo do GFSIS (select name="listaPedidoVendaAnexo[N].tipoAnexo").
// Lidos da tela de anexos; o id vai para o gfsis-gateway (POST /pedidos/anexar-arquivo,
// campo tipoAnexoId). "Comprovante de pagamento" é o padrão.
const TIPOS_ANEXO = [
	{ id: "33570", label: "Comprovante de pagamento" },
	{ id: "33571", label: "Documento de identificação e contratos" },
	{ id: "2", label: "Nota Fiscal" },
	{ id: "3", label: "Arquivo do Certificado" },
	{ id: "1", label: "Termo de Titularidade" },
	{ id: "33569", label: "Termo" },
	{ id: "4", label: "Docs Identificação PF e PJ (não expira)" },
	{ id: "33572", label: "Outros" },
];
const TIPO_ANEXO_PADRAO = "33570";

// Mirrors the mobile CertificateStatusBadge palette (App.js):
// aprovado -> verde claro, aguardando -> ambar claro, demais -> cinza.
const statusBackground = status => {
	const normalized = String(status || "").toLowerCase();
	if (normalized.includes("aprov")) {
		return "#dcfce7";
	}
	if (normalized.includes("aguard")) {
		return "#fef3c7";
	}
	return "#e2e8f0";
};

const formatMoney = value => {
	const normalized = String(value || "").trim();
	if (!normalized) {
		return "-";
	}
	return /r\$/i.test(normalized) ? normalized : `R$ ${normalized}`;
};

const CertificateStatusBadge = ({ status }) => (
	<Chip
		size="small"
		label={status || "Sem status"}
		style={{ backgroundColor: statusBackground(status), color: "#1e293b", fontWeight: 600 }}
	/>
);

const PaymentStatusBadge = ({ paymentStatus }) => {
	const normalized = String(paymentStatus || "").trim().toLowerCase();
	if (!normalized) {
		return null;
	}
	if (normalized.includes("a receber")) {
		return <Chip size="small" label="🕒 A receber" style={{ backgroundColor: "#f59e0b", color: "#fff" }} />;
	}
	if (normalized.includes("conciliad")) {
		return <Chip size="small" label="🟢 Conciliado" style={{ backgroundColor: "#16a34a", color: "#fff" }} />;
	}
	return null;
};

const DeliveryStatusBadge = ({ paymentSentStatus, serviceSentStatus, approvalSentStatus }) => {
	const payment = String(paymentSentStatus || "").trim().toLowerCase();
	const service = String(serviceSentStatus || "").trim().toLowerCase();
	const approval = String(approvalSentStatus || "").trim().toLowerCase();

	const sent = label => (
		<Chip size="small" label={label} style={{ backgroundColor: "#16a34a", color: "#fff" }} />
	);
	const failed = label => (
		<Chip size="small" label={label} style={{ backgroundColor: "#b91c1c", color: "#fff" }} />
	);

	if (approval === "sim") {
		return sent("🟢 Aprovação enviada");
	}
	if (approval.includes("erro")) {
		return failed("✖ Erro ao enviar a aprovação");
	}
	if (service === "sim") {
		return sent("🟢 Atendimento enviado");
	}
	if (service.includes("erro")) {
		return failed("✖ Erro ao enviar o atendimento");
	}
	if (payment === "sim") {
		return sent("🟢 Pagamento enviado");
	}
	if (payment.includes("erro")) {
		return failed("✖ Erro ao enviar o pagamento");
	}
	return null;
};

const DetailLine = ({ label, value }) => (
	<Typography variant="caption" color="textSecondary">
		{label}: {value || "-"}
	</Typography>
);

const CertificateOrderCard = ({ order, actionLabel, onAction, actionLoading, allowAttachReceipt, contactId }) => {
	const classes = useStyles();
	const [expanded, setExpanded] = useState(false);
	const [receiptFile, setReceiptFile] = useState(null);
	const [tipoAnexoId, setTipoAnexoId] = useState(TIPO_ANEXO_PADRAO);
	const [sending, setSending] = useState(false);
	const [attendanceOpen, setAttendanceOpen] = useState(false);
	const [attendanceMode, setAttendanceMode] = useState("video");
	const [attendanceFull, setAttendanceFull] = useState(false);
	const [generateProtocol, setGenerateProtocol] = useState(false);
	const [sendingAttendance, setSendingAttendance] = useState(false);
	const fileInputRef = useRef(null);

	if (!order) {
		return null;
	}

	const handleOpenFilePicker = event => {
		event.stopPropagation();
		if (fileInputRef.current) {
			fileInputRef.current.click();
		}
	};

	const handleFileSelected = event => {
		const file = event.target.files && event.target.files[0];
		if (file) {
			setReceiptFile(file);
		}
	};

	const handleRemoveFile = event => {
		event.stopPropagation();
		setReceiptFile(null);
		if (fileInputRef.current) {
			fileInputRef.current.value = "";
		}
	};

	const handleSendReceipt = async event => {
		event.stopPropagation();
		if (!receiptFile || sending) {
			return;
		}
		if (!order.identifier) {
			toast.error("Pedido sem identificador: não é possível anexar.");
			return;
		}
		setSending(true);
		try {
			const formData = new FormData();
			formData.append("file", receiptFile);
			formData.append("tipoAnexoId", tipoAnexoId);
			await api.post(
				`/certificates/orders/${encodeURIComponent(order.identifier)}/attachment`,
				formData,
				{ headers: { "Content-Type": "multipart/form-data" } }
			);
			toast.success("Comprovante anexado ao pedido.");
			setReceiptFile(null);
			if (fileInputRef.current) {
				fileInputRef.current.value = "";
			}
		} catch (err) {
			toastError(err);
		} finally {
			setSending(false);
		}
	};

	const handleSendAttendance = async event => {
		event.stopPropagation();
		if (sendingAttendance) {
			return;
		}
		if (!contactId) {
			toast.error("Contato não identificado.");
			return;
		}
		if (!order.protocol && !generateProtocol) {
			toast.error("Pedido sem protocolo. Marque 'Gerar protocolo' para gerar.");
			return;
		}
		const cpf = String(order.representativeDocument || order.document || "").replace(/\D/g, "");
		if (!cpf) {
			toast.error("Pedido sem CPF: não é possível montar o link.");
			return;
		}
		setSendingAttendance(true);
		try {
			await api.post(`/certificates/contacts/${encodeURIComponent(contactId)}/attendance`, {
				mode: attendanceMode,
				full: attendanceFull,
				generateProtocol,
				identifier: order.identifier,
				protocol: order.protocol,
				cpf,
				customerName: order.customerName,
				productName: order.productName
			});
			toast.success(
				`Atendimento (${attendanceMode === "video" ? "vídeo" : "online"}) enviado ao cliente.`
			);
			setAttendanceOpen(false);
		} catch (err) {
			toastError(err);
		} finally {
			setSendingAttendance(false);
		}
	};

	return (
		<Paper variant="outlined" className={classes.card} onClick={() => setExpanded(current => !current)}>
			<div className={classes.topRow}>
				<div className={classes.headingText}>
					<Typography variant="subtitle2" noWrap>
						{order.customerName || "Sem nome"} #{order.identifier || "-"}
					</Typography>
					<Typography variant="body2" color="textSecondary">
						{order.productName || "Produto não informado"}
					</Typography>
				</div>
				<CertificateStatusBadge status={order.status} />
			</div>

			<div className={classes.badgesRow}>
				{order.protocol ? (
					<Chip
						size="small"
						label={`Protocolo ${order.protocol}`}
						style={{ backgroundColor: "#3f51b5", color: "#fff" }}
					/>
				) : null}
				<PaymentStatusBadge paymentStatus={order.paymentStatus} />
				<DeliveryStatusBadge
					paymentSentStatus={order.paymentSentStatus}
					serviceSentStatus={order.serviceSentStatus}
					approvalSentStatus={order.approvalSentStatus}
				/>
			</div>

			<Typography variant="body2" color="textSecondary">
				{(order.dateLabel || "-") + " • " + (order.vendor || "Sem vendedor")}
			</Typography>

			{order.orderValue || order.productSuggestedValue ? (
				<div className={classes.valuesRow}>
					<Typography variant="body2" className={classes.orderValue}>
						Valor deste pedido: {formatMoney(order.orderValue)}
					</Typography>
					<Typography variant="caption" color="textSecondary">
						Valor sugerido do produto: {formatMoney(order.productSuggestedValue)}
					</Typography>
				</div>
			) : null}

			<Collapse in={expanded} unmountOnExit>
				<div className={classes.details}>
					<DetailLine label="Protocolo" value={order.protocol} />
					<DetailLine label="Documento" value={order.document} />
					<DetailLine label="Telefone" value={order.phone} />
					<DetailLine label="Email" value={order.email} />
					<DetailLine label="Contabilidade" value={order.accounting} />
					<DetailLine label="Unidade" value={order.unitName || order.unitCode} />
					<DetailLine label="Aprovação" value={order.approvalDate} />
					<DetailLine label="Indicação" value={order.indicationRaw} />
					<DetailLine label="Stage" value={order.stage} />
				</div>

				{allowAttachReceipt ? (
					<div className={classes.attachArea}>
						<Typography variant="caption" color="textSecondary">
							Tipo de anexo
						</Typography>
						<select
							className={classes.attachSelect}
							value={tipoAnexoId}
							disabled={sending}
							onClick={event => event.stopPropagation()}
							onChange={event => setTipoAnexoId(event.target.value)}
						>
							{TIPOS_ANEXO.map(tipo => (
								<option key={tipo.id} value={tipo.id}>
									{tipo.label}
								</option>
							))}
						</select>

						<input
							ref={fileInputRef}
							type="file"
							accept="image/*,application/pdf"
							style={{ display: "none" }}
							onClick={event => event.stopPropagation()}
							onChange={handleFileSelected}
						/>
						<Button
							variant="outlined"
							color="primary"
							size="small"
							className={classes.attachButton}
							disabled={sending}
							onClick={handleOpenFilePicker}
						>
							📎 {receiptFile ? "Trocar arquivo" : "Anexar comprovante"}
						</Button>
						{receiptFile ? (
							<div className={classes.attachFileRow}>
								<Typography variant="caption" className={classes.attachFileName}>
									{receiptFile.name}
								</Typography>
								<Button
									variant="contained"
									color="primary"
									size="small"
									disabled={sending}
									startIcon={sending ? <CircularProgress size={14} color="inherit" /> : null}
									onClick={handleSendReceipt}
								>
									{sending ? "Enviando..." : "Enviar"}
								</Button>
								<Button size="small" disabled={sending} onClick={handleRemoveFile}>
									Remover
								</Button>
							</div>
						) : (
							<Typography variant="caption" color="textSecondary">
								Selecione a imagem ou PDF do comprovante.
							</Typography>
						)}
					</div>
				) : null}

				{allowAttachReceipt ? (
					<div className={classes.attachArea}>
						<Button
							variant="outlined"
							color="primary"
							size="small"
							className={classes.attachButton}
							onClick={event => {
								event.stopPropagation();
								setAttendanceOpen(current => !current);
							}}
						>
							📨 Enviar atendimento
						</Button>
						{attendanceOpen ? (
							<>
								<select
									className={classes.attachSelect}
									value={attendanceMode}
									disabled={sendingAttendance}
									onClick={event => event.stopPropagation()}
									onChange={event => setAttendanceMode(event.target.value)}
								>
									<option value="video">Videoconferência (sala de vídeo)</option>
									<option value="online">Online (emissão pelo portal)</option>
								</select>
								<label className={classes.checkRow} onClick={event => event.stopPropagation()}>
									<input
										type="checkbox"
										checked={generateProtocol}
										disabled={sendingAttendance}
										onChange={event => setGenerateProtocol(event.target.checked)}
									/>
									<Typography variant="caption" color="textSecondary">
										Gerar protocolo (libera o envio no GFSIS e gera o protocolo antes de enviar).
									</Typography>
								</label>
								<label className={classes.checkRow} onClick={event => event.stopPropagation()}>
									<input
										type="checkbox"
										checked={attendanceFull}
										disabled={sendingAttendance}
										onChange={event => setAttendanceFull(event.target.checked)}
									/>
									<Typography variant="caption" color="textSecondary">
										Enviar informações do atendimento (copy completa). Se desmarcado, envia só o link.
									</Typography>
								</label>
								{!order.protocol && !generateProtocol ? (
									<Typography variant="caption" color="error">
										Esse pedido ainda não tem protocolo - marque "Gerar protocolo" para gerar na hora.
									</Typography>
								) : null}
								<Button
									variant="contained"
									color="primary"
									size="small"
									className={classes.attachButton}
									disabled={sendingAttendance || (!order.protocol && !generateProtocol)}
									startIcon={sendingAttendance ? <CircularProgress size={14} color="inherit" /> : null}
									onClick={handleSendAttendance}
								>
									{sendingAttendance ? "Enviando..." : "Enviar atendimento ao cliente"}
								</Button>
							</>
						) : null}
					</div>
				) : null}
			</Collapse>

			{onAction ? (
				<Button
					variant="contained"
					color="primary"
					size="small"
					className={classes.action}
					disabled={actionLoading}
					onClick={event => {
						event.stopPropagation();
						onAction(order);
					}}
				>
					{actionLoading ? "Aplicando..." : actionLabel || "Usar pedido"}
				</Button>
			) : null}
		</Paper>
	);
};

export default CertificateOrderCard;
